import { toggleMobile } from './mobile';

/**
 * Opens the menu by attaching a click event listener to each element with the `data-menu-open` attribute.
 * When an element with the class 'menu-link' and the `data-menu-open` attribute is clicked, the `toggleMenu` function is called.
 */
export function openMenu() {
	getHTMLElements('[data-menu-open]').forEach((element) => {
		if (element.classList.contains('menu-link')) {
			const id = element.dataset.menuOpen;
			if (id) {
				element.addEventListener('click', (event) => {
					toggleMenu(id);
				});
			}
		}
	});
	getHTMLElements('.hamburger').forEach((element) => {
		const mobileMenu = document.getElementById('mobile-menu');
		if (mobileMenu) {
			element.addEventListener('click', (event) => {
				toggleMobile(mobileMenu);
			});
		}
	});
}

/**
 * Toggles the menu visibility based on the provided ID.
 * If the menu is currently open with the given ID, it will be closed.
 * Otherwise, it will be opened and populated with the corresponding content.
 *
 * @param {string} id - The ID of the menu to toggle.
 */
function toggleMenu(id: string) {
	const menuContainer = document.getElementById('mega-menu');
	const menu = document.getElementById('first');
	if (menuContainer instanceof HTMLElement && menu instanceof HTMLElement) {
		if (menuContainer.dataset.current === id) {
			// Close Menu
			menuContainer.dataset.current = '';
			menuContainer.classList.remove('open');
		} else {
			// Open Menu
			populateMenu(menu, id);
			activateButtons(menu);
			showMenu('');
			menuContainer.dataset.current = id;
			menuContainer.classList.add('open');
		}
	}
}

/**
 * Shows the menu with the given ID.
 *
 * @param {string} id - The ID of the menu to show.
 */
function showMenu(id: string) {
	const menu = document.getElementById('second');
	if (menu instanceof HTMLElement) {
		if (id) {
			populateMenu(menu, id);
		} else {
			menu.innerHTML = '';
		}
	}
}

/**
 * Populates the given HTML element with content from a template based on the provided menu ID.
 *
 * @param {HTMLElement} element - The HTML element to populate.
 * @param {string} menuId - The ID of the menu to populate the element with.
 * @returns {string} The parent ID of the template, or an empty string if the template is not found.
 */
export function populateMenu(element: HTMLElement, menuId: string): string {
	const templateId = `template-${menuId}`;
	const template = document.getElementById(templateId);
	element.innerHTML = template?.innerHTML ?? '';

	return template?.dataset.parent ?? '';
}

/**
 * Activates buttons within a given menu, attaching click and mouseover event
 * listeners to elements with the `data-menu-open` and `data-hover-open`
 * attributes, respectively.
 *
 * When an element with the `data-menu-open` attribute is clicked, or an
 * element with the `data-hover-open` attribute is hovered over,
 * the `showMenu` function is called with the corresponding ID.
 *
 * @param {HTMLElement} menu - The menu element to activate buttons within.
 */
function activateButtons(menu: HTMLElement) {
	for (const button of menu.querySelectorAll('[data-menu-open]')) {
		if (button instanceof HTMLElement) {
			const id = button.dataset.menuOpen;
			if (id) {
				button.addEventListener('click', (event) => {
					showMenu(id);
				});
			}
		}
	}
	for (const button of menu.querySelectorAll('[data-hover-open]')) {
		if (button instanceof HTMLElement) {
			const id = button.dataset.hoverOpen;
			if (id) {
				button.addEventListener('mouseover', (event) => {
					showMenu(id);
				});
			}
		}
	}
}

/**
 * Retrieves all HTML elements that match the given identifier.
 *
 * @param {string} identifier - The identifier used to select the elements.
 * @returns {Array<HTMLElement>} An array of HTML elements that match the identifier.
 */
function getHTMLElements(identifier: string): HTMLElement[] {
	const returnList: Array<HTMLElement> = [];
	document.querySelectorAll(identifier).forEach((element) => {
		if (element instanceof HTMLElement) {
			returnList.push(element);
		}
	});
	return returnList;
}
